/* jshint esversion: 8 */
/* jshint node: true */

import React, { useEffect, useState } from "react";
import SecondaryPageContainer from "components/SecondaryPageContainer/SecondaryPageContainer";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import * as common from "common";
import ReactGA from "react-ga4";
import PropTypes from "prop-types";
import { createSidebarLink } from "services/SpotlightTheme";
import { useDispatch, useSelector } from "react-redux";
import { selectTheme } from "stateMgmt/appSlice";
import "./datasets.scss";
import logger from "services/LoggingService";

const s3path = common.getS3Url();

const DatasetsPage = (props) => {
  const dispatch = useDispatch();
  const [tocData, setTocData] = useState([]);
  const theme = useSelector(selectTheme);

  ReactGA.send({
    hitType: "pageview",
    page: "/components/MainContentArea/DatasetsPage/DatasetsPage.js",
    title: "Datasets",
  });

  const pageContents = (
    <div
      className="row DATASETS"
      id="Datasets_page-top"
      data-testid="Datasets_Page"
    >
      <div className="text-left">
        <Typography variant="h1" id="page-heading" role="heading" tabIndex={0}>
          President&apos;s Emergency Plan for AIDS Relief Reporting (PEPFAR)
          Datasets
        </Typography>

        <Typography
          style={{ color: theme.warningColor }}
          tabIndex={0}
          role="note"
        >
          A subset of historic Spotlight datasets through Fiscal Year 2024,
          compliant with Executive Orders, are available below. Additional
          datasets and FY25 results will be made available with the next
          Spotlight update. Note: Fiscal Year 2025 targets and budgets
          as approved through congressional notification procedures for FY25
          are included in these datasets, however program
          interruptions, reporting challenges, and programmatic shifts have
          occurred since January 2025. Fiscal Year 2025 target and budget data
          should be analyzed and interpreted accordingly.
        </Typography>

        <Typography variant="body1">
          These datasets are intended to be downloaded as compressed (zip)
          files, which contain text files, csv (comma separated values) files
          and Microsoft Excel spreadsheet (.xlsx) format and imported into a
          data analyst tool for analysis and review. For more information on
          PEPFAR Spotlight datasets please review this
          <Link
            variant="body2"
            underline="hover"
            style={{ color: theme.linkColor, fontWeight: 500 }}
            href={`${s3path}Spotlight Dataset FAQs.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            FAQ document
          </Link>
          . Codebooks and guidance documents can be found within each
          programmatic section.
        </Typography>

        <section className="section" id="FMD">
          <Typography variant="h2" role="heading" tabIndex={0}>
            Financial Management Datasets
          </Typography>

          <div>
            <Typography>
              PEPFAR Financial Management Datasets include data on budgets and
              expenditures. Budgets are set by country teams in their Country
              and Regional Operational Plans (COP/ROP) and represent the total
              new and applied pipeline financial resources that an implementing
              mechanism plans to spend to achieve approved targets and other
              COP/ROP activities during the 12-month fiscal year
              (October &ndash; September).
            </Typography>
          </div>
          <br/>

          {/* PEPFAROUBudgetsbyFinancialClassifications */}
          <div>
            <div role="note" tabIndex={0}>
              <Typography
                variant="h3"
                role="heading"
                className="page-subheader"
              >
                PEPFAR Operating Unit (OU) Budgets by Financial Classifications
                Dataset
              </Typography>
              <Typography>
                The PEPFAR Operating Unit (OU) Budgets by Financial
                Classifications dataset provides, at an Operating Unit (OU)
                level, what was budgeted by Program Area for Care & Treatment,
                HIV Testing Services, Prevention, Socioeconomic, and Above Site
                Programs for Fiscal Year (FY) 2004 &ndash; 2025. Budgets are further
                disaggregated by Sub-Program, Beneficiary and Sub-Beneficiary
                (pre-COP23), Targeted Beneficiary (COP23 onwards), Interaction
                type (Service vs. Non-Service Delivery), Record Type
                (Implementing Mechanism vs. Management & Operations), and an
                indication of whether the funding is for Economic Support Fund
                (American Rescue Plan Act) Funding (Yes/No). Allocated
                Beneficiary funding (COP23 onwards) is currently blank in the
                dataset due to recent reporting guidance changes impacting the
                age/sex disaggregation required for the Allocated Beneficiary
                calculations. These may be added back to the dataset in the
                future.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="h3">
                <Typography variant="body1">
                  <Link
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 500 }}
                    href={`${s3path}fin_mngt/budget_by_program/PEPFAROUBudgetsbyFinancialClassifications.zip`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {" "}
                    PEPFAR Operating Unit (OU) Budgets by Financial
                    Classifications.zip
                  </Link>
                </Typography>
              </Typography>
            </div>
          </div>

          <br />

          {/* PEPFARProgramExpenditures */}
          <div>
            <div role="navigation" tabIndex={0}>
              <Typography variant="h3" role="heading">
                PEPFAR Program Expenditures Dataset
              </Typography>
              <Typography>
                The PEPFAR Program Expenditures dataset provides Operating Unit
                (OU) level expenditures from Fiscal Year 2015 &ndash; 2024 by Program
                Area for Care & Treatment, HIV Testing Services, Prevention,
                Socioeconomic, and Above Site Programs. Expenditures are further
                disaggregated by Sub-Program, Beneficiary and Sub-Beneficiary
                (pre-COP23), Interaction type (Service vs. Non-Service
                Delivery), Cost Category, and Sub-Cost Category. Expenditures
                for COP23 onwards were reported by Targeted Beneficiary, which
                will be included in the dataset in a future release. Fiscal Year
                2018 &ndash; 2023 expenditures were reported against the financial
                classifications for Program, Sub-Program, Beneficiary,
                Sub-Beneficiary, Cost Category, and Sub-Cost Category. Fiscal
                Year 2015 &ndash; 2017 expenditures were reported through expenditure
                analysis (EA) classifications and mapped to the new financial
                classifications.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 600 }}
                  href={`${s3path}fin_mngt/pepfar_expenditures_by_program/PEPFARProgramExpenditures.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PEPFAR Program Expenditures.zip
                </Link>
              </Typography>
            </div>
          </div>

          <br />
          {/* Public_Partner_Budget_by_Financial_Classifications */}
          <div>
            <div role="heading" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Public Partner Budget by Financial Classifications Dataset
              </Typography>
              <Typography>
                The Public Partner Budget by Financial Classifications dataset
                provides mechanism level budget data for Fiscal Year 2021 &ndash; 2025
                by the PEPFAR Financial Classifications. Budgets are
                disaggregated by Program, Sub-Program, Beneficiary and
                Sub-Beneficiary (pre-COP 23), Targeted Beneficiary (COP 23
                onwards), Interaction Type (Service vs Non-Service Delivery),
                and Cross-Cutting Attributes. Additional metadata, including
                Operating Unit, Country, Country ISO code, Planning Cycle,
                Implementation Year, Mechanism Code, Mechanism Name, Funding
                Agency, Partner Name, Award Number, Award Start Data, Award End
                Date, Procurement Type, and Record Type (Implementing Mechanism
                vs. Management & Operations) are also available for each
                mechanism. Please note that for any TBD partners, some mechanism
                details are redacted and budgets are aggregated to the OU by
                agency level such that the amount of each TBD mechanism is not
                known.{" "}
              </Typography>
              <Typography>
                The COP budget represents the total resources (regardless of
                whether they are new resources (Global AIDS Programs, HHS/CDC;
                Global Health Programs, State; or Global Health Programs, USAID)
                or prior year applied pipeline resources) that a mechanism plans
                to outlay to achieve approved targets and other COP/ROP
                activities during the 12-month fiscal year. Applied Pipeline
                funds are funds that were previously programmed and are being
                carried forward into a new planning year. All outlays
                anticipated for all mechanisms (new, continuing, and closing)
                occurring during the implementation period are included within
                the planning level. In this dataset, the Program Management
                budget for each mechanism has been &#34;masked&#34; to protect
                the sensitivity of this information. Program Management amounts
                are distributed proportionately to interventions within
                the mechanism. For mechanisms that contain solely Program
                Management interventions, such as those with only the
                Sub-program of “Program Management: IM Closeout Costs”, the
                mechanism’s identifying information and intervention details
                have been redacted and the budget amount have been aggregated to
                the total OU level. Cross-cutting attribute data for any given
                mechanism’s Program Management intervention are distributed to
                the other interventions in that mechanism or aggregated to a
                single row. The actual Program Management amount for each OU can
                be found in the OU Budget by Financial Classifications dataset,
                but this data will not be available at the mechanism level.
              </Typography>
            </div>
            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 500 }}
                  href={`${s3path}additionalData/partner-budget-data/Public_Partner_Budget_by_Financial_Classifications.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Public_Partner_Budget_by_Financial_Classifications.zip
                </Link>
              </Typography>
            </div>
          </div>
          <br />
        </section>

        <section className="section" id="MDD">
          <div role="heading" tabIndex={0}>
            <Typography variant="h2">
              Monitoring, Evaluation, and Reporting (MER) Downloadable Datasets
            </Typography>

            <Typography>
              The Monitoring, Evaluation and Reporting (MER) Datasets provide a
              view of an Operating Unit&apos;s (OU) targets and results against a
              selection of indicators that demonstrate PEPFAR’s progress and
              contributions to combat global HIV/AIDS. The targets and results
              presented in these datasets represent the collective targets and
              achievements for all U.S. Government PEPFAR implementing agencies.
              For more information on the indicators by which targets
              and results are reported, please consult the latest MER Indicator
              Reference Guide found
              <Link
                variant="body2"
                underline="hover"
                style={{ color: theme.linkColor, fontWeight: 600 }}
                href="https://help.datim.org/hc/en-us/articles/360000084446-MER-Indicator-Reference-Guides"
                target="_blank"
                rel="noopener noreferrer"
              >
                here.
              </Link>
            </Typography>
            <Typography>
              Please note for Monitoring, Evaluation, and Reporting (MER)
              Datasets: Due to data quality improvement efforts, Nigeria Fiscal
              Year 23 Quarter 4 (Q4) MER data are excluded from all datasets.
            </Typography>
          </div>
          <br/>

          {/* Enhanced_Geographical_Analysis */}
          <div>
            <div role="heading" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Enhanced Geographical Analysis &ndash; Sub-National Unit
              </Typography>
              <Typography>
                Quarterly MER results (FY2016 &ndash; 2024) and annual targets
                (FY2016 &ndash; 2025) by Operating Unit and Sub-National Units for
                a comprehensive array of indicators across PEPFAR&#39;s program
                areas.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 600 }}
                  href={`${s3path}mer_downloadables/Enhanced_Geographical_Analysis.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Enhanced_Geographical_Analysis.zip
                </Link>
              </Typography>
            </div>
          </div>

          <br />
          {/* Operating_Unit_by_Fine_Age_and_Sex */}
          <div>
            <div role="heading" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Operating Unit (OU) by Fine Age & Sex
              </Typography>
              <Typography>
                Quarterly MER results (FY2019 &ndash; 2024) and annual targets
                (FY2019 &ndash; 2025) by Operating Unit, Fine Age and Sex. Note:
                Reporting for age bands above 50 is optional and not available
                for all OUs; however, the aggregate 50+ age band is available
                for all OUs.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 600 }}
                  href={`${s3path}mer_downloadables/Operating_Unit_by_Fine_Age_and_Sex.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Operating Unit (OU) by Fine Age & Sex
                </Link>
              </Typography>
            </div>
          </div>

          <br />
          {/* Clinical_Cascade_Results_by_Coarse_Age_and_Sex */}
          <div>
            <div role="heading" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Clinical Cascade – Results by Coarse Age and Sex
              </Typography>
              <Typography>
                Quarterly MER results (FY2016 &ndash; 2024) and annual targets
                (FY2016 &ndash; 2025), Sub-National Unit, Coarse Age and Sex for the
                following subset of Testing and Treatment indicators: HTS_TST
                (people receiving testing and counseling services), HTS_SELF
                (HIV self-test kits distributed), HTS_TST_POS (people
                testing positive for HIV), TX_CURR (people currently receiving
                Antiretroviral Therapy (ART)), TX_NEW (people newly enrolled on
                ART), TX_PVLS (number of individuals on
                ART with a suppressed Viral Load result
                [&lt; 1,000 copies/mL] within the past 12 months), and
                TX_NET_NEW (net change in number of people on ART).
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 600 }}
                  href={`${s3path}mer_downloadables/Clinical_Cascade_Results_by_Coarse_Age_and_Sex.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Clinical_Cascade_Results_by_Coarse_Age_and_Sex.zip
                </Link>
              </Typography>
            </div>
          </div>

          <br />
          {/* Public_Partner_MER_AgeSex */}
          <div>
            <div role="heading" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Public Partner MER Dataset
              </Typography>
              <Typography>
                Quarterly Implementing Partner (IP) MER results (FY2018 &ndash; 2024)
                and annual targets (FY2018 &ndash; 2025) disaggregated by Coarse Age and
                Sex.
              </Typography>
              <Typography>
                The Age/Sex dataset includes all data reported to PEPFAR with
                Age and Sex information. Though MER Guidance includes finer age
                bands, this file includes aggregate age bands of &lt;15, 15+,
                and Unknown Age. Indicators that are not reported by age and sex
                are excluded from this dataset.
              </Typography>
              <Typography>
                MER data is not included for any partners which are not active
                or TBD. When two IPs operate at the same facility, any duplicate
                results reported at that facility are manually &apos;deduplicated&apos;
                by PEPFAR staff to generate an aggregate total of individuals
                reached at the facility. This aggregate total is used to produce
                country-level results. Due to the nature of this data set at the
                partner level, this manual deduplication adjustment has been
                removed from this dataset so IP data can be reviewed in full. Do
                not aggregate partner data to generate total country or SNU
                results.{" "}
              </Typography>
            </div>
            <div role="heading">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 500 }}
                  href={`${s3path}additionalData/partner-mer-data/Public_Partner_MER_AgeSex.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Public_Partner_MER_AgeSex.zip
                </Link>
              </Typography>
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  useEffect(() => {
    renderTOC();
  }, []);

  function renderTOC() {
    let sectionElement, headerElement, title, link;
    const sectionHeaders = document.getElementsByClassName("section");
    const links = [];
    for (let i = 0; i < sectionHeaders.length; i++) {
      logger.debug(`create link ${i}`);
      sectionElement = sectionHeaders[i];
      headerElement = sectionElement.getElementsByTagName("h2")[0];
      if (headerElement) {
        title = headerElement.innerText;
        link = "#" + sectionElement.id;
        links.push(createSidebarLink(link, title, theme.linkColor));
      }
    }
    setTocData(links);
  }

  return (
    <SecondaryPageContainer
      contents={pageContents}
      showToc={true}
      pageHeaderId="page-heading"
      isSidebarCollapsed={props.isSidebarCollapsed}
      data={tocData}
    />
  );
};

DatasetsPage.propTypes = {
  isSidebarCollapsed: PropTypes.bool.isRequired,
};

export default DatasetsPage;
